import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PatientTab from "./patient/patient-tab";
import VOBTab from "./vob/vob-tab";
import EstimateTab from "./estimate/estimate-tab";
import RiskTab from "./risk/risk-tab";
import {Tabs, Tab, Box, Grid, Divider, Typography, CircularProgress} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { IconLabel } from "@finpay-development/shared-components";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "src/shared/state/root-reducer";
import { AppDispatch } from "../../shared/state/store";
import { getAllPatientCurrentInsurances, getPatientDemographics, ensureSelectedEncounterForEstimatesView } from "@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk";
import {loadPatientVobList} from "@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk";
import {getSavedEstimates,} from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/estimator-thunk';
import "../tab-styling.scss"
import {Utils} from '@finpay-development/shared-platform/lib/esm/shared/utils';
import _ from 'lodash';
import {useParams} from 'react-router';
import { showErrorStatus,showStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { setSelectedVob } from '@finpay-development/shared-platform/lib/esm/shared/state/vob-slice';
import { isSelfPayPayor, loadAndSetRateAsWithoutVob } from "../../shared/utils/insurance-utils";
import {Client, Estimate, Facility, Payor, Plan, RateAsPayorMap, Vob} from "@finpay-estimation/estimation-types";
import { Dispatch } from "redux";
import { ensureEstimatesLinkedToCurrentIoc, loadSavedEstimatesListv3, loadSelectedEstimate } from "@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk";
import { setSelectedEstimate } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import { v3EstimateErrorMsgs } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { loadAndSetRateAsPayorPlan, resolvePayorPlanForRateAsLoad } from '../utils/payor-plan-utils';
import { resolveDefaultFacilityId } from './vob/vob-form/utils/vob-utils';
import { EstimateValidationService } from '../utils/estimate-validation';
import { getEstimatorFacilityPayers } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-thunk";
import { getPlanDetails, getSelfPayPayer } from "../../admissions-advisor/components/inbound/launchV2Utils";
import {EstimatesSearchModal} from '@finpay-development/shared-platform/lib/esm/estimates/components/estimate-search/estimates-search-modal';

function EstimatesView() {
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const navigate = useNavigate();
    const [vobShellReady, setVobShellReady] = useState(false);
    const lastVobShellPathRef = useRef<string | null>(null);
    const { patientId, estimateId, vobId } = useParams<{
        patientId?: string;
        estimateId?: string;
        vobId?: string;
      }>();
    const tabPaths = ["", "vob", "estimate", "risk"];
    const tabNames = ["Patient", "VOB", "Estimate", "Risk"];
    const pathParts = location.pathname.split("/");
    const currentTab = pathParts.length > 3 ? pathParts[3] : "";
    const initialTabIndex = tabPaths.indexOf(currentTab);
    const [tabIndex, setTabIndex] = useState(initialTabIndex === -1 ? 0 : initialTabIndex);

    const vobs = useSelector((state: RootState) => state.vobContext.patientVobList || []);
    const vobsLoading = useSelector((state: RootState) => state.vobContext.isLoading?.patientVobList || false);
    
    const selectedPatient = useSelector(
        (state: RootState) => state.patientContext.selectedPatient
    );
    const currentVobFromRedux = useSelector(
        (state: RootState) => state.vobContext.selectedVob
    );
    const savedEstimations = useSelector(
        (state: RootState) => state.estimatorContext.savedEstimatesList || []
    );
    const savedEstimatesLoading = useSelector(
        (state: RootState) => state.estimatorContext.isLoading?.savedEstimatesList || false
    );
    const selectedPatientCurrentInsurance = useSelector(
        (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    );
    const isPatientCurrentInsuranceLoading = useSelector(
        (state: RootState) => state.patientContext.isLoading?.patientCurrentInsurance || false
    );
    const reduxRateAsPayorMap = useSelector((state: RootState) => state.estimatorContext.rateAsPayorMap) || {};
    const reduxMasterPayors = useSelector((state: RootState) => state.adminContext.adminConfigurationContext?.payersMap) || new Map();
    const selectedEstimate = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate
    );
    const isSelectedEstimateLoading = useSelector(
        (state: RootState) => state.estimatorContext.isLoading?.selectedEstimate || false
    );
    const isSelectedVobLoading = useSelector(
        (state: RootState) => state.vobContext.isLoading?.selectedVob || false
    );
    const payersMap = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext.payersMap
    );
    const isLoadingPayer = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext.isLoading.isLoadingPayer
    );
    const payers = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext?.payers
    );
    const allClientsMap = useSelector((state: RootState) => {
        return state.implementationContext?.implementationSpecialistClient.allClientsMap;
    });
    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );
    const selectedVob = useSelector(
        (state: RootState) => state.vobContext.selectedVob
    );
    const rateAsPayorPlan = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorPlan
    );
    const rateAsPayorMap = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorMap
    );
    const facilityLevelsOfCareList = useSelector(
        (state: RootState) => state.estimatorContext.facilityLevelsOfCareList
    );
    const selectedPatientId = selectedPatient.patientId === 0 ? 1 : selectedPatient.patientId;

    const fetchData = async () => {
        if (!isLoadingPayer) {
            const pathParts = location.pathname.split('/');
            const current = pathParts.length > 3 ? pathParts[3] : '';
            const idx = pathParts.length > 6 ? 3 : tabPaths.indexOf(current);
            const isVobRoute = idx === 1;
            if (isVobRoute) {
                const vobPathKey = location.pathname;
                if (lastVobShellPathRef.current !== vobPathKey) {
                    lastVobShellPathRef.current = vobPathKey;
                    setVobShellReady(false);
                }
            }
            //0 = Patient, 1 = VOB, 2 = Estimate, 3 = Risk
            // if patientId exists in the URL we need GET patient Info
            if (patientId) {
                let vobListResponse: any;
                let estimateResp: any;
                let selectedEstimate: Estimate;
                let patientDemographicResp: any;
                let patientInsuranceResp: any;

                if (+patientId !== selectedPatient.patientId) {
                    patientDemographicResp = await dispatch(getPatientDemographics(+patientId));
                }
                if (+patientId !== selectedPatientCurrentInsurance.patientId) {
                    patientInsuranceResp = await dispatch(
                        getAllPatientCurrentInsurances({patientId: +patientId})
                    );
                }
                // Load the Patient VOB List
                vobListResponse = await dispatch(
                    loadPatientVobList(Number(pathParts[2]))
                );
                if (idx === 2 || idx === 3) {
                    // Estimate or Risk tab is in URL 
                    // First check to see if ID exists on the URL
                    if (estimateId) {
                        estimateResp = await dispatch(
                            loadSelectedEstimate({
                                paramId: -2,
                                estimationId: estimateId!,
                            })
                        );
                        selectedEstimate = estimateResp?.payload;
                        const currentPayorId = patientInsuranceResp?.payload[0]?.payorId || selectedPatientCurrentInsurance.payorId;
                        const isExistingEstimateSelfPay = estimateResp?.payload?.isSelfPay;  
                        const isSelfPay = isSelfPayPayor(currentPayorId, payersMap);
                        if (isExistingEstimateSelfPay || isSelfPay || selectedEstimate?.isPrivatePay) {
                            // if self pay:
                            const sssconfig: any = {
                                paramId: -2,
                                masterListPayers: payers,
                                facilityId: selectedEstimate.facilityId,
                            };
                            const facilityPayors = await dispatch(getEstimatorFacilityPayers(sssconfig));
                            const selfPayPayor = getSelfPayPayer(facilityPayors);
                            const selectedPlan = getPlanDetails(selfPayPayor, facilityPayors);
                            let updatedPatientCurrentInsurance = {
                                ...selectedPatientCurrentInsurance,
                                patientCurrentInsuranceId: 1,
                                payorId: selfPayPayor?.cfgPayorId,
                                payorPlanId: selectedPlan?.payorPlanId,
                            }
                            // Create an adapter function with the signature expected by loadAndSetRateAsWithoutVob
                            const loadAndSetRateAsPayorPlanAdapter = async (
                                facility: any,
                                dispatch: Dispatch<any>,
                                payers: any,
                                payor: Payor,
                                plan: Plan
                            ): Promise<void> => {
                                const clientId = selectedEstimate?.clientId || selectedPatient?.clientId ? Number(selectedEstimate?.clientId || selectedPatient?.clientId) : 0;
                                const clientObj = { clientId } as Client;
                                const matchedVob = vobListResponse?.payload.find(
                                    (v: any) => v?.vobId === estimateResp?.payload?.vobId
                                );
                                if (!selectedEstimate?.facilityId) {
                                    console.error('Selected estimate does not have a facilityId');
                                    return;
                                }
                                const targetClient = allClientsMap.get(Number(clientId));
                                const facilityFromClient = targetClient?.clientFacilities?.find(
                                    (f: any) => f.facilityId === Number(selectedEstimate.facilityId)
                                );
                                const estimateFacility: Facility = facilityFromClient 
                                    ? { 
                                        facilityId: facilityFromClient.facilityId, 
                                        facilityName: facilityFromClient.facilityName 
                                    } as Facility
                                    : { facilityId: Number(selectedEstimate.facilityId) } as Facility;
                                await loadAndSetRateAsPayorPlan(
                                    clientObj,
                                    estimateFacility,
                                    dispatch,
                                    reduxMasterPayors,
                                    payor,
                                    plan,
                                    reduxRateAsPayorMap || {},
                                    estimateResp?.payload!,
                                    rateAsPayorPlan!,
                                    matchedVob!,
                                    estimateResp?.payload,
                                    estimateResp?.payload?.vobId
                                );
                            };
                            const patientDetails = patientDemographicResp?.payload?.patientDetails;
                            await loadAndSetRateAsWithoutVob({
                                selectedPatient: patientDetails,
                                patientDemographicResp: null,
                                selectedPatientCurrentInsurance: updatedPatientCurrentInsurance,
                                patientInsuranceResp: null,
                                allClientsMap,
                                dispatch,
                                payers,
                                loadAndSetRateAsPayorPlan: loadAndSetRateAsPayorPlanAdapter,
                            });
                            await dispatch(
                                loadSavedEstimatesListv3({
                                    paramId: -2,
                                    patientId: Number(patientId),
                                })
                            );                                   
                        } else {
                            // else not SelfPay:
                            if (!selectedEstimate?.vobId) {
                                dispatch(showErrorStatus(v3EstimateErrorMsgs.noEstimate));
                                return;
                            }
                            // Find and set the matching VOB
                            const selectedVob = vobListResponse?.payload.find(
                                (vob: Vob) => vob.vobId === Number(selectedEstimate.vobId)
                            );
                            if (!selectedVob) {
                                dispatch(showErrorStatus(v3EstimateErrorMsgs.noVob));
                                return;
                            }

                            dispatch(setSelectedVob(selectedVob));
                            const resolvedPayorPlan = resolvePayorPlanForRateAsLoad(selectedVob, selectedEstimate);
                            if (!resolvedPayorPlan) {
                                dispatch(showErrorStatus(v3EstimateErrorMsgs.noVob));
                                return;
                            }
                            await loadAndSetRateAsPayorPlan(
                                selectedVob.client,
                                selectedVob.facility,
                                dispatch,
                                payersMap,
                                resolvedPayorPlan.payor,
                                resolvedPayorPlan.plan,
                                rateAsPayorMap || {},
                                selectedEstimate,
                                selectedEstimate?.rateAsPayorPlan ?? rateAsPayorPlan ?? undefined,
                                selectedVob,
                                selectedEstimate,
                                selectedVob.vobId
                            );
                        }                            
                    } else {
                        // This is a New Estimate Instance
                        // If selectedVob exists in state - us it
                        // If not - set top VOB of the patient's VOB list
                        const vobToUse = currentVobFromRedux?.vobId
                            ? currentVobFromRedux
                            : vobListResponse?.payload[0];
                        const currentPayorId = patientInsuranceResp?.payload[0]?.payorId || selectedPatientCurrentInsurance.payorId;
                        const isSelfPay = isSelfPayPayor(currentPayorId, payersMap);

                        // First checking to see if SelfPay is true
                        if (isSelfPay) {
                            // Handling SelfPay Estimate
                            // Call LoadandSet util w/o Vob
                            // Create an adapter function with the signature expected by loadAndSetRateAsWithoutVob
                            const loadAndSetRateAsPayorPlanAdapter = async (
                                facility: any,
                                dispatch: Dispatch<any>,
                                payers: any,
                                payor: Payor,
                                plan: Plan
                            ): Promise<void> => {
                                // Find the client based on the facility
                                const clientId = selectedPatient?.clientId ? Number(selectedPatient.clientId) : 0;
                                const clientObj = { clientId } as Client;

                                await loadAndSetRateAsPayorPlan(
                                    clientObj,
                                    facility,
                                    dispatch,
                                    reduxMasterPayors,
                                    payor,
                                    plan,
                                    reduxRateAsPayorMap || {}
                                );
                            };

                            await loadAndSetRateAsWithoutVob({
                                selectedPatient,
                                patientDemographicResp,
                                selectedPatientCurrentInsurance,
                                patientInsuranceResp,
                                allClientsMap,
                                dispatch,
                                payers,
                                loadAndSetRateAsPayorPlan: loadAndSetRateAsPayorPlanAdapter,
                            });                                      
                            // getting estimate list using patient ID.  
                            await dispatch(
                                loadSavedEstimatesListv3({
                                    paramId: -2,
                                    patientId: Number(patientId),
                                })
                            );
                        } else {
                            if (!vobToUse?.vobId) {
                                //  since estimate Id isnt present, we need to leave estimate blank                            
                                // if neither exist we need to redirect user back to VOB screen
                                dispatch(showErrorStatus(v3EstimateErrorMsgs.completeVob));
                                navigate(`/patients/${patientId}/vob`);
                                return;
                            }
                            if (!isSelfPay && !currentVobFromRedux?.vobId) {
                                dispatch(setSelectedVob(vobToUse));
                            }
                            await loadAndSetRateAsPayorPlan(
                                vobToUse.client,
                                vobToUse.facility,
                                dispatch,
                                reduxMasterPayors || {},
                                vobToUse.payor,
                                vobToUse.plan,
                                reduxRateAsPayorMap || {},
                                undefined,
                                rateAsPayorPlan ?? undefined,
                                vobToUse
                            );
                            // getting estimate list using patient ID.  
                            await dispatch(
                                loadSavedEstimatesListv3({
                                    paramId: -2,
                                    patientId: Number(patientId),
                                })
                            );
                        }
                    }
                } else if (idx === 1 && vobId) {
                    // VOB ID exists in URL
                    // Set selected VOB
                    const selectedVob = vobListResponse?.payload.find(
                        (vob: Vob) => vob.vobId === Number(vobId)
                    );
                    if (!selectedVob) {
                        dispatch(showErrorStatus(v3EstimateErrorMsgs.noVob));
                        setVobShellReady(true);
                        return;
                    }
                    dispatch(setSelectedVob(selectedVob));
                    await loadAndSetRateAsPayorPlan(
                        selectedVob.client,
                        selectedVob.facility,
                        dispatch,
                        reduxMasterPayors,
                        selectedVob.payor,
                        selectedVob.plan,
                        reduxRateAsPayorMap,
                        undefined,
                        rateAsPayorPlan ?? undefined,
                        selectedVob
                    );
                    // Load estimates for the selected patient
                    const estimatesList = await dispatch(
                        loadSavedEstimatesListv3({
                            paramId: -2,
                            patientId: Number(patientId),
                        })
                    );
                    const selectedEstimate = estimatesList?.payload?.[0];
                    // Set selected estimate
                    if (selectedEstimate) {
                        setSelectedEstimate(selectedEstimate);
                    }
                } else if (idx === 1 && !vobId) {
                    // On VOB URL but no VOB selected
                    if (!selectedPatient.clientId && !patientDemographicResp?.payload?.patientDetails?.clientId) {
                        // Skipping — patient data not ready yet
                        return;
                    }
                    const targetClientId =
                        +selectedPatient.clientId! !== 0
                            ? selectedPatient.clientId
                            : patientDemographicResp?.payload?.patientDetails?.clientId;

                    const targetInsurance =
                        selectedPatientCurrentInsurance ||
                        patientInsuranceResp?.payload?.[0];

                    const targetClient = allClientsMap?.get(
                        Number(targetClientId)
                    );

                    const defaultFacilityId = resolveDefaultFacilityId(
                        targetClient?.clientFacilities,
                        selectedEncounter?.facilityId
                    );
                    const targetFacility = targetClient?.clientFacilities?.find(
                        (f: { facilityId: number }) => Number(f.facilityId) === Number(defaultFacilityId)
                    );
                    if (targetFacility && targetInsurance) {
                        const selectedPayor: Payor = {
                            payorId: targetInsurance.payorId,
                        };
                        const selectedPlan: Plan = {
                            payorPlanId: targetInsurance.payorPlanId,
                        };
                        // Create a client object from the targetClient
                        const clientObj = { clientId: targetClient?.clientId || 0 } as Client;
                        const facilityForRateAs: Facility = {
                            facilityId: targetFacility.facilityId,
                            facilityName: targetFacility.facilityName,
                        };

                        await loadAndSetRateAsPayorPlan(
                            clientObj,
                            facilityForRateAs,
                            dispatch,
                            reduxMasterPayors,
                            selectedPayor,
                            selectedPlan,
                            reduxRateAsPayorMap || {}
                        );
                    }
                }
                // If Redux has no selectedEncounter for this patient, get or create IOC and set it
                await dispatch(
                  ensureSelectedEncounterForEstimatesView({
                    patientId: Number(patientId),
                    pathname: location.pathname,
                  })
                );
                if (isVobRoute) {
                    setVobShellReady(true);
                }
                // When on Estimate tab/URL, link any estimates without IOC to current selectedEncounter
                if (location.pathname.includes('/estimate')) {
                  await dispatch(ensureEstimatesLinkedToCurrentIoc(Number(patientId)));
                }
                // Deep link with ?op=pay only: /patients/:id/estimate/:id?op=pay → Hub IOC when billing engine is V3
                const opPay =
                    new URLSearchParams(location.search).get('op') === 'pay';
                if (
                    opPay &&
                    /^\/patients\/[^/]+\/estimate\/[^/]+$/.test(
                        location.pathname
                    )
                ) {
                    const s = store.getState();
                    const est = s.estimatorContext.selectedEstimate;
                    const enc = s.patientContext.selectedEncounter;
                    const map =
                        s.implementationContext?.implementationSpecialistClient
                            ?.allClientsMap;
                    const estIdFromUrl = estimateId;
                    const estAny = est as
                        | {pfrEstimateId?: number; estimateId?: number}
                        | undefined;
                    const loadedEstimateId =
                        estAny?.pfrEstimateId ?? estAny?.estimateId;
                    const estimateMatchesUrl =
                        loadedEstimateId != null &&
                        estIdFromUrl != null &&
                        String(loadedEstimateId) === String(estIdFromUrl);
                    const cid =
                        est?.clientId != null ? Number(est.clientId) : NaN;
                    const clientVm = Number.isFinite(cid)
                        ? map?.get(cid)
                        : undefined;
                    const isBillingEngineV3 =
                        clientVm?.billingEngineVersion?.toUpperCase() === 'V3';
                    if (
                        estimateMatchesUrl &&
                        isBillingEngineV3 &&
                        enc?.patientEncounterId &&
                        enc.patientId === Number(patientId)
                    ) {
                        navigate(
                            `/hub/patient/${enc.patientId}/ioc/${enc.patientEncounterId}?ch=crm`,
                            {replace: true}
                        );
                        return;
                    }
                }
            }
            if (idx !== -1 && idx !== tabIndex && !location.pathname.includes('/risk')) {
                setTabIndex(idx);
            }
        }
        
    };

    useEffect(() => {
        const ready =
          location.pathname &&
          !isLoadingPayer &&
          allClientsMap?.size > 0;
      
        if (!ready) return;
      
        fetchData();
    }, [location.pathname, isLoadingPayer, allClientsMap?.size, selectedPatient?.patientId, selectedPatient?.clientId]);

    useEffect(() => {
        if (!location.pathname.includes("/vob")) {
            lastVobShellPathRef.current = null;
        }
    }, [location.pathname]);

    const estimatesBootstrapReady =
        Boolean(location.pathname) && !isLoadingPayer && (allClientsMap?.size ?? 0) > 0;
    const vobTabRouteReady = estimatesBootstrapReady && vobShellReady;

    useEffect(()=>{
        //Add the patientId to the url on-mount if it exists in Redux but doesn't exist in the url
        if (selectedPatient.patientId && !patientId && tabIndex ===0) {
            navigate(`/patients/${selectedPatient.patientId}`, { replace: true });
        }
    }, [])

    // Add a useEffect to handle insurance changes
    useEffect(() => {
        // If we're on the patient tab and insurance changes from self-pay to regular
        if (tabIndex === 0 && selectedPatientCurrentInsurance?.payorId) {
            // Check if the insurance is no longer self-pay
            const isSelfPay = isSelfPayPayor(selectedPatientCurrentInsurance.payorId, payersMap);
            console.log('Insurance changed, isSelfPay:', isSelfPay);
            
            // If VOB tab was previously disabled due to self-pay but now should be enabled,
            // we don't need to do anything special - the isVobTabDisabled memo will update automatically
        }
    }, [selectedPatientCurrentInsurance?.payorId, payersMap]);

    // Add estimate validation on load based on loading states
    useEffect(() => {
        // Only validate when we have an estimate loaded and are on estimate/risk tabs
        if (!selectedEstimate || (tabIndex !== 2 && tabIndex !== 3)) {
            return;
        }

        // Don't validate if any relevant data is still loading
        if (isSelectedEstimateLoading || isSelectedVobLoading || isPatientCurrentInsuranceLoading || isLoadingPayer) {
            return;
        }

        // Check if VOB is still loading based on estimate's vobId vs loaded VOB
        const estimateHasVobId = selectedEstimate?.vobId;
        const isVobDataMismatch = estimateHasVobId && (!selectedVob || selectedVob.vobId !== selectedEstimate.vobId);
        
        // Defer validation if VOB data doesn't match estimate
        if (isVobDataMismatch) {
            return;
        }

        // Validate the loaded estimate
        const validationContext = {
            estimate: selectedEstimate,
            selectedPatientCurrentInsurance,
            selectedEncounter,
            vob: selectedVob,
            rateAsPayorPlan,
            payersMap,
            facilityLevelsOfCareList
        };

        const validationResult = EstimateValidationService.validateEstimateOnLoad(validationContext);

        // Show warnings to admissions agents
        if (validationResult.warnings.length > 0) {
            validationResult.warnings.forEach(warning => {
                dispatch(showErrorStatus(warning));
            });
        }

        // Show any critical errors 
        if (validationResult.errors.length > 0) {
            validationResult.errors.forEach(error => {
                dispatch(showErrorStatus(error));
            });
        }
    }, [
        selectedEstimate, 
        selectedPatientCurrentInsurance, 
        selectedEncounter, 
        selectedVob, 
        rateAsPayorPlan, 
        payersMap, 
        facilityLevelsOfCareList,
        tabIndex,
        isSelectedEstimateLoading,
        isSelectedVobLoading,
        isPatientCurrentInsuranceLoading,
        isLoadingPayer
    ]);

    const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
        // If Patient ID doesnt exist - no tab change
        if (!selectedPatient.patientId) {
            return;
        }

        // Patient Tab is clicked
        if (newValue === 0) {
            navigate(`/patients${selectedPatientId > 1 ? '/' + selectedPatientId : ""}`);
        } else if (newValue === 3) {
            // Risk tab clicked - navigate to risk tab with current estimate ID
            if (estimateId) {
                navigate(`/patients/${patientId}/estimate/${estimateId}/risk`);
            } else {
                // If no estimate ID, show error
                dispatch(showErrorStatus("Please select an estimate before accessing the Risk tab"));
                return;
            }
        } else if (newValue === 1) {
            // VOB tab clicked
            console.log('Estimates View - VOB tab clicked:', {
                isPatientCurrentInsuranceLoading,
                selectedPatientCurrentInsurance,
                payorId: selectedPatientCurrentInsurance?.payorId
            });
            
            // Check if patient has current insurance set (only if data is not loading)
            if (!isPatientCurrentInsuranceLoading && (!selectedPatientCurrentInsurance || selectedPatientCurrentInsurance.payorId <= 0)) {
                console.log('Estimates View - Blocking VOB tab access due to missing insurance');
                // Don't navigate to VOB tab if no insurance is set
                setTabIndex(0); // Reset to patient tab
                dispatch(showStatus("Please set the patient's current insurance before accessing the VOB tab"));
                return;
            }
            
            // Check if patient has self-pay insurance
            if (!isPatientCurrentInsuranceLoading && isSelfPayPayor(selectedPatientCurrentInsurance?.payorId, payersMap)) {
                console.log('Estimates View - Blocking VOB tab access due to self-pay insurance');
                // Don't navigate to VOB tab if patient has self-pay insurance
                setTabIndex(0); // Reset to patient tab
                dispatch(showStatus("VOB is not available for self-pay patients"));
                return;
            }
            
            // Fetch VOBs if we don't already have them
            if (vobs.length === 0) {
                console.log(
                    "Loading Patient VOB List from tab",
                    "color:rgb(175, 142, 76); font-weight: bold; font-size: 14px; background: #E8F5E9; padding: 4px 8px; border-radius: 4px;"
                  );
                await dispatch(loadPatientVobList(selectedPatientId));
            }

            const pathParts = location.pathname.split("/");
            let currentVobId = null;

            if (pathParts.includes("vob")) {
                const vobIndex = pathParts.indexOf("vob");
                if (vobIndex + 1 < pathParts.length) {
                    const potentialVobId = pathParts[vobIndex + 1];
                    if (potentialVobId !== "vob" && potentialVobId !== "estimate" && potentialVobId !== "risk") {
                        currentVobId = potentialVobId;
                    }
                }
            }

            if (!currentVobId) {
                if (currentVobFromRedux?.vobId) {
                    currentVobId = String(currentVobFromRedux.vobId);
                } else {
                    const firstValidVob = vobs.find(vob => vob.isValid);
                    if (firstValidVob) {
                        currentVobId = String(firstValidVob.vobId);
                    }
                }
            }
            
            if (currentVobId && currentVobId !== "vob" && currentVobId !== "estimate") {
                navigate(`/patients/${selectedPatientId}/vob/${currentVobId}`);
            } else {
                navigate(`/patients/${selectedPatientId}/vob`);
            }
        } else if (newValue === 2) {
            // Estimate tab clicked
            // HAWK TODO check to see if this patient is selfPay and if so, ignore the error
            const isSelfPay = isSelfPayPayor(selectedPatientCurrentInsurance.payorId, payersMap);
            if (!currentVobFromRedux?.vobId && vobs.length === 0 && !isSelfPay) {
                dispatch(showErrorStatus(v3EstimateErrorMsgs.completeVob));
                return;
            }
            const vobToUse = currentVobFromRedux;

            // Check if we have a selectedEstimate that matches the current VOB
            if (selectedEstimate && selectedEstimate.vobId === vobToUse?.vobId) {
                // We have a matching estimate, navigate to it
                setTabIndex(2) // setting the tab makes the page load smoother
                navigate(`/patients/${selectedPatientId}/estimate/${selectedEstimate.estimateId}`);
                return;
            }
            
            // Fetch estimates if we don't already have them or they don't match the current VOB
            if ((savedEstimations.length === 0 || !savedEstimations.some(est => est.vobId === vobToUse?.vobId)) && !savedEstimatesLoading) {
                if (vobToUse?.vobId) {
                    
                    const estimatesResult = await dispatch(getSavedEstimates({
                        paramId: -2,
                        vobId: vobToUse.vobId
                    }));

                    console.log("Estimates linked to vob", estimatesResult.payload);
                    
                    // Select the first one
                    if (estimatesResult.payload && estimatesResult.payload.length > 0) {
                        const firstEstimate = estimatesResult.payload[0];
                        if (firstEstimate.pfrEstimateId) {
                            // Navigate to the specific estimate with its ID
                            navigate(`/patients/${selectedPatientId}/estimate/${firstEstimate.pfrEstimateId}`);
                            return;
                        }
                    }
                }
            } else if (savedEstimations.length > 0) {
                // If we already have estimates for this VOB, select the first one that matches
                const matchingEstimate = savedEstimations.find(est => est.vobId === vobToUse?.vobId);
                if (matchingEstimate && matchingEstimate.estimateId) {
                    navigate(`/patients/${selectedPatientId}/estimate/${matchingEstimate.estimateId}`);
                    return;
                }
                // Fallback to first estimate if no VOB match
                const firstEstimate = savedEstimations[0];
                if (firstEstimate.estimateId) {
                    navigate(`/patients/${selectedPatientId}/estimate/${firstEstimate.estimateId}`);
                    return;
                }
            }
            // Fallback to general estimate page
            navigate(`/patients/${selectedPatientId}/${tabPaths[newValue]}`);
        } else {
            navigate(`/patients/${selectedPatientId}/${tabPaths[newValue]}`);
        }
        setTabIndex(newValue);
    };

    const patientInfoHeader = (
        <>
            <Grid
                item
                container
                alignItems="center"
                style={{
                    fontSize: "18px"
                }}
            >
                <Grid item className="profile-icon">
                    <IconLabel
                        icon={<PersonIcon style={{fontSize: "36px"}} test-id="patient-tab-header-icon"  />}
                        label={`${_.capitalize(selectedPatient?.contact?.preferredName || selectedPatient?.contact.firstName)} ${_.capitalize(selectedPatient?.contact.lastName)}`}
                        isBold={true}
                    />
                </Grid>
                <Grid item className="icon-label">
                    <Typography sx={{ fontSize: '18px', marginLeft: "28px" }}>
                        {selectedPatient?.contact.email}
                    </Typography>
                </Grid>
                <Grid item className="icon-label">
                    <Typography sx={{ fontSize: '18px', marginLeft: "28px" }}>
                        {Utils.formatPhoneNumber(selectedPatient?.contact.primPhoneNum || "")}
                    </Typography>
                </Grid>
            </Grid>
        </>
    );

    // Calculate if VOB tab should be disabled
    const isVobTabDisabled = useMemo(() => {
        // Disable if patient insurance is loading
        if (isPatientCurrentInsuranceLoading) return true;

        // Disable if no insurance is set
        if (!selectedPatientCurrentInsurance || selectedPatientCurrentInsurance.payorId <= 0) return true;

        // Disable if patient has self-pay insurance
        const isSelfPay = isSelfPayPayor(selectedPatientCurrentInsurance?.payorId, payersMap);
        console.log('Checking if VOB tab should be disabled:', {
            isPatientCurrentInsuranceLoading,
            selectedPatientCurrentInsurance,
            payorId: selectedPatientCurrentInsurance?.payorId,
            isSelfPay
        });
        return isSelfPay;
    }, [isPatientCurrentInsuranceLoading, selectedPatientCurrentInsurance?.payorId]);

    // Calculate if Risk tab should be disabled
    const isRiskTabDisabled = useMemo(() => {
        // Disable if no estimate is selected
        return !selectedEstimate || !selectedEstimate.estimateId;
    }, [selectedEstimate]);

    // Send screen name to Google Analytics when tab changes
    useEffect(() => {
        if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function' && tabIndex >= 0 && tabIndex < tabNames.length) {
            const screenName = tabNames[tabIndex];
            // Send as custom event for GA4 web property
            (window as any).gtag('event', 'screen_view', {
                'screen_name': screenName,
                'screen_class': 'EstimatesView'
            });
        }
    }, [tabIndex]);

    const urlPatientId = patientId ? Number(patientId) : 0;
    const isPatientsRouteWithPatientId =
        Number.isFinite(urlPatientId) && urlPatientId > 0;
    const patientsDeepLinkBlocking =
        isPatientsRouteWithPatientId &&
        (!estimatesBootstrapReady || selectedPatient.patientId !== urlPatientId);

    if (patientsDeepLinkBlocking) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh",
                    width: "100%",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Grid
                container
                alignItems="center"
                style={{ padding: "30px 40px 0 40px", margin: "4px 20px 20px 20px" }}
                wrap="nowrap"
            >
                <Grid item xs={8}>
                    {selectedPatientId > 1 ? patientInfoHeader: <Box pt={5} />}
                </Grid>
                <Grid
                    item
                    className="icon-label"
                    xs={4}
                >
                    <EstimatesSearchModal />
                </Grid>
            </Grid>
            <Grid item xs={12} className="tabs estimate-tabs">
                <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    ml: 4, 
                    border: "0px"
                }}>
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 0,
                            '& .MuiTabs-indicator': { display: 'none' },
                            border: "0px"
                        }}
                    >
                        {["Patient", "VOB", "Estimate", "Risk"].map((label, idx) => (
                            <Tab
                                key={label}
                                label={label}
                                disabled={
                                    (label === "VOB" && !selectedPatient.patientId) || 
                                    (label === "VOB" && isVobTabDisabled) ||
                                    (label === "Risk" && isRiskTabDisabled)
                                }
                                sx={{
                                    fontWeight: tabIndex === idx ? 'bold' : 'normal',
                                    fontSize: "16px",
                                    color: tabIndex === idx ? '#297EC1' : '#666',
                                    '&.Mui-selected': {
                                        fontWeight: 'bold',
                                        color: '#297EC1',
                                    },
                                    '&.Mui-disabled': {
                                        color: '#ccc',
                                        cursor: 'not-allowed',
                                    },
                                    borderBottom: "0px"
                                }}
                                test-id={`finadvisor-tab-${label}`}
                            />
                        ))}
                    </Tabs>
                    
                </Box>
            </Grid>
            <Divider style={{margin: "0 26px"}} />
            <Box>
                {tabIndex === 0 && <PatientTab />}
                {tabIndex === 1 && (
                    <VOBTab isVobRouteReady={vobTabRouteReady} />
                )}
                {tabIndex === 2 && <EstimateTab />}
                {tabIndex === 3 && <RiskTab />}
            </Box>
        </Box>
    );
}

export default EstimatesView;

